import React, { useState, useEffect, useCallback } from "react";
import "./Home.css";
import { Link } from "react-router-dom";
import Testimonials from "../Testimonials";
import SEO from "../SEO";
import Hours from "../Hours";
import { business, fullAddress } from "../../seo/business";
import { services, locations, slugify } from "../../seo/routes";

// Fixed image for top-left slot
const FIXED_IMAGE = "/imgs/hero/office-alina.webp";

// Rotating images for the other 4 slots
const rotatingImages = [
  "/imgs/hero/office-1.webp",
  "/imgs/hero/office-2.webp",
  "/imgs/hero/office-3.webp",
  "/imgs/hero/office-4.webp",
  "/imgs/hero/office-5.webp",
  "/imgs/hero/office-6.webp",
  "/imgs/hero/office-7.webp",
  "/imgs/hero/office-8.webp",
  "/imgs/hero/office-9.webp",
  "/imgs/hero/office-10.webp",
  "/imgs/hero/office-11.webp",
  "/imgs/hero/office-12.webp",
  "/imgs/hero/office-13.webp",
];

// Accepted PPO plans shown on the home page (dimensions prevent layout shift)
const insurances = [
  { name: "Aetna", file: "Aetna-logo.png", width: 199, height: 64 },
  { name: "Cigna", file: "cigna-logo.png", width: 184, height: 83 },
  { name: "Guardian", file: "Guardian-logo.png", width: 224, height: 45 },
  { name: "UnitedHealthcare", file: "UnitedHealthcare-logo.png", width: 227, height: 74 },
  { name: "MetLife", file: "MetLife-logo.png", width: 203, height: 46 },
  { name: "EmblemHealth", file: "EmblemHealth-logo.png", width: 405, height: 156 },
  { name: "Delta Dental", file: "DeltaDental-logo.png", width: 1500, height: 179 },
  { name: "Anthem", file: "anthem-logo.webp", width: 697, height: 158 },
];

// Slots that rotate (1, 2, 3, 4) - slot 0 is fixed
const ROTATING_SLOTS = [1, 2, 3, 4];

// Each hero photo also exists at 480px and 800px wide (office-1-480.webp...)
const heroSrcSet = (src) => {
  const base = src.replace(/\.webp$/, "");
  return `${base}-480.webp 480w, ${base}-800.webp 800w, ${src} 1250w`;
};

// Rendered width of each tile (see Home.css). Slot 1 is the wide tile on
// desktop; slot 4 spans the full width on phones.
const SLOT_SIZES = {
  0: "(max-width: 680px) 250px, 360px",
  1: "(max-width: 680px) 250px, 650px",
  2: "(max-width: 680px) 250px, 360px",
  3: "(max-width: 680px) 250px, 360px",
  4: "(max-width: 680px) 100vw, 360px",
};

// The largest tile in each layout is the page's main image, so fetch it first
const PRIORITY_SLOTS = [1, 4];

const HeroCollage = () => {
  // Track current and previous images for crossfade effect
  // Each slot has { current: imageIndex, previous: imageIndex | null }
  const [slotImages, setSlotImages] = useState([
    { current: 0, previous: null },
    { current: 1, previous: null },
    { current: 2, previous: null },
    { current: 3, previous: null },
  ]);
  // Track which rotating slot to replace next
  const [slotIndex, setSlotIndex] = useState(0);
  // Track which image from the pool to use next
  const [nextImageIndex, setNextImageIndex] = useState(4);

  const replaceImage = useCallback(() => {
    setSlotImages((prev) => {
      const newSlots = [...prev];
      newSlots[slotIndex] = {
        current: nextImageIndex,
        previous: prev[slotIndex].current,
      };
      return newSlots;
    });

    // Clear the previous image after animation completes
    setTimeout(() => {
      setSlotImages((prev) => {
        const newSlots = [...prev];
        newSlots[slotIndex] = {
          ...newSlots[slotIndex],
          previous: null,
        };
        return newSlots;
      });
    }, 600);

    // Move to next rotating slot
    setSlotIndex((prev) => (prev + 1) % ROTATING_SLOTS.length);
    // Move to next image in the pool
    setNextImageIndex((prev) => (prev + 1) % rotatingImages.length);
  }, [slotIndex, nextImageIndex]);

  useEffect(() => {
    const interval = setInterval(() => {
      replaceImage();
    }, 5000);

    return () => clearInterval(interval);
  }, [replaceImage]);

  const getImagesForSlot = (slot) => {
    if (slot === 0) return { current: FIXED_IMAGE, previous: null };
    const rotatingIndex = ROTATING_SLOTS.indexOf(slot);
    const slotData = slotImages[rotatingIndex];
    return {
      current: rotatingImages[slotData.current],
      previous: slotData.previous !== null ? rotatingImages[slotData.previous] : null,
    };
  };

  return (
    <div className="hero-collage" role="img" aria-label="Benson Dental Studio office photos">
      <div className="collage-grid">
        {[0, 1, 2, 3, 4].map((slot) => {
          const images = getImagesForSlot(slot);
          return (
            <div
              key={slot}
              className={`collage-item collage-item-${slot} ${slot === 0 ? "fixed" : ""}`}
            >
              {images.previous && (
                <img
                  src={images.previous}
                  srcSet={heroSrcSet(images.previous)}
                  sizes={SLOT_SIZES[slot]}
                  alt=""
                  className="collage-img previous"
                  aria-hidden="true"
                />
              )}
              <img
                key={images.current}
                src={images.current}
                srcSet={heroSrcSet(images.current)}
                sizes={SLOT_SIZES[slot]}
                alt={`Inside Benson Dental Studio in Bensonhurst, Brooklyn (${slot + 1} of 5)`}
                className={`collage-img current ${images.previous ? "fading-in" : ""}`}
                width="1252"
                height="707"
                fetchpriority={PRIORITY_SLOTS.includes(slot) ? "high" : undefined}
              />
            </div>
          );
        })}
      </div>
      <div className="hero-overlay">
        <header className="hero">
          <span className="hero-title">
            <p className="hero-phone">
              <a href={`tel:${business.phone}`} aria-label={`Call us at ${business.phone}`}>
                {business.phone}
              </a>
            </p>
          </span>
        </header>
      </div>
    </div>
  );
};

const Home = () => (
  <main className="home-main-container fade-in" id="main-content" role="main">
    <SEO
      fullTitle="Dentist in Bensonhurst, Brooklyn | Benson Dental Studio"
      description="Benson Dental Studio is a modern dentist in Bensonhurst, Brooklyn serving Sheepshead Bay, Bay Ridge & South Brooklyn. We speak English, Russian & Ukrainian. Call 718-266-5889."
      canonical="/"
    />
    <section className="home-container">
      <HeroCollage />
    </section>
    <section className="home-container">
      <article className="welcome-message">
        <h1>Your Dentist in Bensonhurst, Brooklyn</h1>
        <p className="welcome-address">
          Come visit us at {fullAddress}.
        </p>

        <p>
          At Benson Dental Studio, your smile is our priority. Our modern office
          in Bensonhurst offers cleanings and checkups, cosmetic dentistry,
          crowns, root canals, gum care, and dental implants, all under one
          roof. Patients come to us from across South Brooklyn, including
          Sheepshead Bay, Bay Ridge, Gravesend, and Bath Beach.
        </p>
        <p>
          Our team speaks English, Russian, and Ukrainian. We are on the corner
          of 23rd Ave, a short walk from the Bay Parkway and 25th Avenue D train
          stations.
        </p>
        <h2>Hours</h2>
        <Hours />
        <section className="insurances-container">
          <h2>Insurances</h2>
          <p>
            We work with most major insurances in NY. If your insurance is not
            listed below, please call and we'll check for you.{" "}
          </p>
          <section className="insurance-logos-container">
            {insurances.map((insurance) => (
              <img
                key={insurance.name}
                alt={`${insurance.name} dental insurance accepted`}
                className="insurance-logo"
                src={`/imgs/insurances/${insurance.file}`}
                width={insurance.width}
                height={insurance.height}
                loading="lazy"
              />
            ))}
          </section>
        </section>
      </article>
      <section className="home-map-container">
        <iframe
          title="Our Location on Google Maps"
          src={business.mapEmbedUrl}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </section>
    </section>

    <Testimonials />

    <section className="services-summary" aria-labelledby="services-summary-heading">
      <h2 id="services-summary-heading">Our Dental Services in Brooklyn</h2>
      <div className="service-categories">
        {services.map((service) => (
          <article key={service.title} className="service-category">
            <h3>
              <Link to={`/services/${slugify(service.title)}`}>
                {service.title} Dentistry
              </Link>
            </h3>
            <ul>
              {service.cards.map((card) => (
                <li key={card.title}>{card.title}</li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </section>

    <section className="neighborhoods-summary" aria-labelledby="neighborhoods-heading">
      <h2 id="neighborhoods-heading">Neighborhoods We Serve</h2>
      <ul className="neighborhood-links">
        {locations.map((location) => (
          <li key={location.slug}>
            <Link to={`/${location.slug}`}>{location.seoTitle}</Link>
          </li>
        ))}
      </ul>
      <div className="cta-banner">
        <p>
          Looking for a dentist in Bensonhurst, Sheepshead Bay, Bay Ridge, or
          anywhere in South Brooklyn? Call us at{" "}
          <a href={`tel:${business.phone}`}>{business.phone}</a> to schedule
          your appointment.
        </p>
      </div>
    </section>
  </main>
);

export default Home;
